import React, { useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useStore } from '../store/useStore';
import { ListTodo, MessageSquare, Edit2, InboxIcon, SendIcon } from 'lucide-react';
import { TaskList } from '../components/tasks/TaskList';
import { TaskEditModal } from '../components/tasks/TaskEditModal';
import { isExecutive } from '../utils/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';

export const TasksPage: React.FC = () => {
  const { currentUser, tasks, messages, availableUsers } = useStore();
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>('received');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Get tasks assigned to and by current user
  const receivedTasks = tasks.filter(task => task.assignedTo === currentUser?.id);
  const sentTasks = tasks.filter(task => task.assignedBy === currentUser?.id);
  
  // Get related messages
  const taskMessages = messages.filter(msg => 
    msg.type === 'DIRECT' && 
    msg.departments.includes(currentUser?.department || '') &&
    msg.title.toLowerCase().includes('task')
  );
  
  // Check if user has edit permissions
  const canEdit = currentUser && (isExecutive(currentUser.role) || currentUser.role === 'MANAGER');

  // Helper function to get user name from ID
  const getUserName = (userId: string) => {
    const user = availableUsers.find(u => u.id === userId);
    return user?.name || 'Unknown User';
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-6 mb-6">
          <div className="flex items-center gap-3">
            <img
              src={currentUser?.avatar}
              alt={currentUser?.name}
              className="w-12 h-12 sm:w-16 sm:h-16 rounded-full ring-4 ring-emerald-500 ring-offset-4"
            />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Tasks</h1>
              <p className="text-sm sm:text-base text-gray-600">Manage your tasks and assignments</p>
            </div>
          </div>
          {canEdit && (
            <Button
              variant="primary"
              onClick={() => setIsEditModalOpen(true)}
              icon={<Edit2 className="w-4 h-4" />}
              className="self-start sm:self-auto"
            >
              Edit Tasks
            </Button>
          )}
        </div>

        {/* Mobile Tab Navigation */}
        <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 -mx-4 sm:mx-0 px-4 sm:px-0">
          <Button
            variant={activeTab === 'received' ? 'primary' : 'ghost'}
            onClick={() => setActiveTab('received')}
            icon={<InboxIcon className="w-4 h-4" />}
            className="whitespace-nowrap"
          >
            Received ({receivedTasks.length})
          </Button>
          <Button
            variant={activeTab === 'sent' ? 'primary' : 'ghost'}
            onClick={() => setActiveTab('sent')}
            icon={<SendIcon className="w-4 h-4" />}
            className="whitespace-nowrap"
          >
            Sent ({sentTasks.length})
          </Button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'received' ? (
            <motion.div
              key="received"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-6"
            >
              <Card className="p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <ListTodo className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h2 className="text-lg font-semibold">Assigned Tasks</h2>
                </div>
                <TaskList tasks={receivedTasks} showDepartment />
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                  </div>
                  <h2 className="text-lg font-semibold">Task Messages</h2>
                </div>
                <div className="space-y-4">
                  {taskMessages.length > 0 ? (
                    taskMessages.map(message => (
                      <div key={message.id} className="p-4 bg-gray-50 rounded-lg">
                        <h3 className="font-medium text-gray-900 line-clamp-1">{message.title}</h3>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{message.content}</p>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs text-gray-500">
                          <span className="inline-flex items-center">
                            From: {message.sender.name}
                          </span>
                          <span className="hidden sm:inline">•</span>
                          <span className="inline-flex items-center">
                            {format(new Date(message.createdAt), 'MMM d, h:mm a')}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-8">No task messages</p>
                  )}
                </div>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              key="sent"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-6"
            >
              <Card className="p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <ListTodo className="w-5 h-5 text-purple-600" />
                  </div>
                  <h2 className="text-lg font-semibold">Tasks You've Assigned</h2>
                </div>
                <TaskList tasks={sentTasks} showDepartment />
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-amber-600" />
                  </div>
                  <h2 className="text-lg font-semibold">Task Updates</h2>
                </div>
                <div className="space-y-4">
                  {sentTasks.map(task => (
                    <div key={task.id} className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-medium text-gray-900 line-clamp-1">{task.title}</h3>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{task.description}</p>
                      <div className="flex flex-wrap items-center justify-between mt-2">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                          <span className="inline-flex items-center">
                            Assigned to: {getUserName(task.assignedTo)}
                          </span>
                          <span className="hidden sm:inline">•</span>
                          <span className="inline-flex items-center">
                            Due: {format(new Date(task.dueDate), 'MMM d, h:mm a')}
                          </span>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          task.status === 'COMPLETED' 
                            ? 'bg-emerald-100 text-emerald-700'
                            : task.status === 'IN_PROGRESS'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {task.status}
                        </span>
                      </div>
                    </div>
                  ))}
                  {sentTasks.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No tasks assigned yet</p>
                  )}
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <TaskEditModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />
    </div>
  );
};